import React from 'react';
import styled from 'styled-components/macro';

import useMedia from 'hooks/useMedia';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import ComponentControls from 'components/styled/ComponentControls/ComponentControls';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import { FakeDropdown } from 'components/base/SkeletonLoader/FakeInteractive';
import Box from 'components/elements/Box';
import { CompareModeType } from './types';
import { GendersTabs, ComparePieWrapper } from './styles';
import { tabOptions } from '../shared/shared';
import Tabs from 'components/base/Tabs/Tabs';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';

const DonutLoader = styled('div')<any>`
  @keyframes donut-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: inline-block;
  border: ${({ compareMode }): string =>
    `${compareMode ? '10px' : '20px'} solid rgba(0, 0, 0, 0.1)`};
  border-left-color: #bdc9d6;
  border-radius: 50%;
  width: ${({ compareMode }): string => (compareMode ? '90px' : '144px')};
  height: ${({ compareMode }): string => (compareMode ? '90px' : '144px')};
  animation: donut-spin 1.2s linear infinite;
  order: ${({ isSmallScreen }): number => (isSmallScreen ? 1 : 2)};
`;

const DonutLoaderSection: React.FC<any> = ({
  isSmallScreen,
  isTabletScreen,
  compareMode,
  keyValueCount,
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection={isSmallScreen ? 'column' : 'row'}
      justifyContent={isSmallScreen ? 'center' : 'initial'}
      mt={isTabletScreen ? 6 : compareMode ? 4 : 0}
      mb={compareMode ? 4 : 'auto'}
    >
      <Box
        maxWidth="240px"
        display="flex"
        flexWrap="wrap"
        my={2}
        mr={isSmallScreen ? 0 : compareMode ? 2 : 8}
        order={isSmallScreen ? 2 : 1}
      >
        {Array.from(Array(keyValueCount).keys()).map((id: number) => (
          <Box key={id} pr={2} pb={2} flexBasis="50%">
            <KeyValueLoader />
          </Box>
        ))}
      </Box>

      <DonutLoader isSmallScreen={isSmallScreen} compareMode={compareMode} />
    </Box>
  );
};

interface ISkeletonLoaderProps {
  isNarrowScreen?: boolean;
  keyValueCount?: number;
  showPeriodDropdown?: boolean;
  showToolbarDropdown?: boolean;
  title: string;
  compareMode?: CompareModeType;
  activeView?: 'table' | 'graph';
  elements?: any[];
}

const SkeletonLoader: React.FC<ISkeletonLoaderProps> = (props) => {
  const {
    isNarrowScreen,
    keyValueCount,
    showPeriodDropdown,
    showToolbarDropdown,
    title,
    compareMode,
    activeView,
    elements,
  } = props;

  const isSmallScreen = useMedia('(max-width: 40em)');
  const isTabletScreen = useMedia('(max-width: 50em)');

  return (
    <GenericComponent
      title={title}
      loading={true}
      isNarrowScreen={isNarrowScreen}
      showToolbarDropdown={showToolbarDropdown}
    >
      <AnimatedLoadingWrapper loading={true}>
        {showPeriodDropdown && (
          <ComponentControls>
            <FakeDropdown>Last 7 Days</FakeDropdown>
          </ComponentControls>
        )}

        {compareMode && (
          <GendersTabs isSmallScreen={isSmallScreen}>
            <Tabs
              componentName="gender-breakdown-fake"
              options={tabOptions}
              selectedOption={activeView ? activeView : tabOptions[0].value}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onChange={(): void => {}}
            />
          </GendersTabs>
        )}

        {!compareMode ? (
          <DonutLoaderSection
            isSmallScreen={isSmallScreen}
            isTabletScreen={isTabletScreen}
            compareMode={compareMode}
            keyValueCount={keyValueCount}
          />
        ) : (
          // Compare loader

          <Box
            flexDirection={isSmallScreen ? 'column' : 'row'}
            justifyContent={
              isSmallScreen
                ? 'center'
                : compareMode && activeView === 'graph'
                ? 'space-between'
                : 'initial'
            }
            mt={isTabletScreen ? 6 : 3}
            flexWrap={compareMode && activeView === 'graph' ? 'wrap' : 'nowrap'}
          >
            {activeView === 'graph' ? (
              !isSmallScreen ? (
                elements?.map(
                  (id, index): JSX.Element => (
                    <ComparePieWrapper
                      key={id + index}
                      display="inline-block"
                      width="50%"
                    >
                      <DonutLoaderSection
                        isSmallScreen={isTabletScreen}
                        isTabletScreen={isTabletScreen}
                        compareMode={compareMode}
                        keyValueCount={keyValueCount}
                      />
                    </ComparePieWrapper>
                  )
                )
              ) : (
                <DonutLoaderSection
                  isSmallScreen={isSmallScreen}
                  isTabletScreen={isTabletScreen}
                  compareMode={compareMode}
                  keyValueCount={keyValueCount}
                />
              )
            ) : (
              <table>
                <tbody>
                  <TableLoader columnCount={5} />
                </tbody>
              </table>
            )}
          </Box>
        )}
      </AnimatedLoadingWrapper>
    </GenericComponent>
  );
};

export default SkeletonLoader;
